import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Server, Settings, Crosshair, Package } from 'lucide-react';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    title: 'Información del',
    titleHighlight: 'Servidor',
    serverRates: 'Tasas del Servidor',
    experienceRate: 'Tasa de Experiencia',
    dropRate: 'Tasa de Drop',
    zenDrop: 'Drop de Zen',
    gameSettings: 'Configuración del Juego',
    settings: [{
      label: 'Versión',
      value: 'Temporada 6 Ep 3'
    }, {
      label: 'Nivel Máx',
      value: '400'
    }, {
      label: 'Reset Máx',
      value: '50'
    }, {
      label: 'Stats Máx',
      value: '32,767'
    }, {
      label: 'Puntos / Nivel',
      value: '5 / 7'
    }, {
      label: 'Crear Gremio',
      value: 'Nivel 150'
    }, {
      label: 'Magic Gladiator',
      value: 'Nivel 220'
    }, {
      label: 'Dark Lord',
      value: 'Nivel 250'
    }]
  },
  en: {
    title: 'Server',
    titleHighlight: 'Information',
    serverRates: 'Server Rates',
    experienceRate: 'Experience Rate',
    dropRate: 'Drop Rate',
    zenDrop: 'Zen Drop',
    gameSettings: 'Game Settings',
    settings: [{
      label: 'Version',
      value: 'Season 6 Ep 3'
    }, {
      label: 'Max Level',
      value: '400'
    }, {
      label: 'Max Reset',
      value: '50'
    }, {
      label: 'Max Stats',
      value: '32,767'
    }, {
      label: 'Points / Level',
      value: '5 / 7'
    }, {
      label: 'Guild Create',
      value: 'Level 150'
    }, {
      label: 'Magic Gladiator',
      value: 'Level 220'
    }, {
      label: 'Dark Lord',
      value: 'Level 250'
    }]
  },
  pt: {
    title: 'Informações do',
    titleHighlight: 'Servidor',
    serverRates: 'Taxas do Servidor',
    experienceRate: 'Taxa de Experiência',
    dropRate: 'Taxa de Drop',
    zenDrop: 'Drop de Zen',
    gameSettings: 'Configurações do Jogo',
    settings: [{
      label: 'Versão',
      value: 'Temporada 6 Ep 3'
    }, {
      label: 'Nível Máx',
      value: '400'
    }, {
      label: 'Reset Máx',
      value: '50'
    }, {
      label: 'Stats Máx',
      value: '32,767'
    }, {
      label: 'Pontos / Nível',
      value: '5 / 7'
    }, {
      label: 'Criar Guilda',
      value: 'Nível 150'
    }, {
      label: 'Magic Gladiator',
      value: 'Nível 220'
    }, {
      label: 'Dark Lord',
      value: 'Nível 250'
    }]
  }
};
export function ServerInfo() {
  const [language, setLanguage] = useState<Language>('es');
  const t = translations[language];
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang) setLanguage(savedLang);
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  return <section className="py-16 md:py-24 relative z-10 bg-darkerBlue border-t border-white/5" data-id="element-554">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-555">
        <div className="text-center mb-16" data-id="element-556">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" data-id="element-557">
            {t.title}{' '}
            <span className="text-gradient-gold" data-id="element-558">{t.titleHighlight}</span>
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          scale: 0
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="w-24 h-1 bg-gold mx-auto rounded-full" data-id="element-559" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-560">
          {/* Rates Column */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="glass-panel p-8 md:p-10 rounded-2xl border-l-4 border-l-glowBlue relative overflow-hidden" data-id="element-561">
            <div className="absolute top-0 right-0 p-6 opacity-10" data-id="element-562">
              <Server className="w-32 h-32" data-id="element-563" />
            </div>

            <h3 className="text-2xl font-bold text-white mb-8 flex items-center gap-3 relative z-10" data-id="element-564">
              <Settings className="text-glowBlue" data-id="element-565" />
              {t.serverRates}
            </h3>

            <div className="space-y-6 relative z-10" data-id="element-566">
              <div data-id="element-567">
                <div className="flex justify-between text-sm mb-2" data-id="element-568">
                  <span className="text-gray-300 font-bold uppercase tracking-wider" data-id="element-569">
                    {t.experienceRate}
                  </span>
                  <span className="text-glowBlue font-bold" data-id="element-570">1000x</span>
                </div>
                <div className="w-full bg-darkBlue rounded-full h-2 border border-white/5" data-id="element-571">
                  <div className="bg-glowBlue h-2 rounded-full" style={{
                  width: '85%'
                }} data-id="element-572"></div>
                </div>
              </div>

              <div data-id="element-573">
                <div className="flex justify-between text-sm mb-2" data-id="element-574">
                  <span className="text-gray-300 font-bold uppercase tracking-wider" data-id="element-575">
                    {t.dropRate}
                  </span>
                  <span className="text-gold font-bold" data-id="element-576">50%</span>
                </div>
                <div className="w-full bg-darkBlue rounded-full h-2 border border-white/5" data-id="element-577">
                  <div className="bg-gold h-2 rounded-full" style={{
                  width: '50%'
                }} data-id="element-578"></div>
                </div>
              </div>

              <div data-id="element-579">
                <div className="flex justify-between text-sm mb-2" data-id="element-580">
                  <span className="text-gray-300 font-bold uppercase tracking-wider" data-id="element-581">
                    {t.zenDrop}
                  </span>
                  <span className="text-green-400 font-bold" data-id="element-582">100%</span>
                </div>
                <div className="w-full bg-darkBlue rounded-full h-2 border border-white/5" data-id="element-583">
                  <div className="bg-green-400 h-2 rounded-full" style={{
                  width: '100%'
                }} data-id="element-584"></div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Features List Column */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="glass-panel p-8 md:p-10 rounded-2xl border-l-4 border-l-gold relative overflow-hidden" data-id="element-585">
            <div className="absolute top-0 right-0 p-6 opacity-10" data-id="element-586">
              <Crosshair className="w-32 h-32" data-id="element-587" />
            </div>

            <h3 className="text-2xl font-bold text-white mb-8 flex items-center gap-3 relative z-10" data-id="element-588">
              <Package className="text-gold" data-id="element-589" />
              {t.gameSettings}
            </h3>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 relative z-10" data-id="element-590">
              {t.settings.map((item, index) => <li key={index} className="bg-darkBlue/50 border border-white/5 rounded-lg p-4 flex flex-col" data-id="element-591">
                  <span className="text-xs text-gray-400 uppercase tracking-wider mb-1" data-id="element-592">
                    {item.label}
                  </span>
                  <span className="text-white font-bold font-cinzel" data-id="element-593">
                    {item.value}
                  </span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
}